/**
 * ProjectWorkspacePage — Workspace do projeto (Task 13).
 *
 * Header + Tabs controladas (Gantt | Lista | Configurações). A aba Gantt usa o
 * adapter `TroiaGantt` (único importador de `@svar-ui/react-gantt`) com painel
 * lateral; o clique numa barra abre o `TaskDrawer`; o drag persiste datas via
 * `useUpdateProjectTask` (o refetch do React Query re-hidrata o Gantt).
 */

import React, { Suspense, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, StatusPill, MemberAvatarList, Button, Tabs, CardActionsMenu } from '@/shared/components/ui';
import type { TabItem } from '@/shared/components/ui';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';

import { useProjectGantt, useUpdateProjectTask } from '../hooks';
import { getProjectStatusPill } from '../components/ProjectCard';
import type { TroiaGanttZoom } from '../components/gantt/types';

// Lazy: isola o SVAR (biblioteca pesada) num chunk próprio, fora do bundle
// síncrono principal — só é baixado ao abrir o workspace de um projeto.
const TroiaGantt = React.lazy(() =>
  import('../components/gantt/TroiaGantt').then((m) => ({ default: m.TroiaGantt })),
);
import { WorkspaceSidePanel } from '../components/WorkspaceSidePanel';
import { TaskDrawer } from '../components/TaskDrawer';
import { TaskModal, type TaskModalMode } from '../components/TaskModal';
import { ProjectTasksTable } from '../components/ProjectTasksTable';
import { ProjectConfigTab } from '../components/ProjectConfigTab';
import { TimelineTab } from '../components/TimelineTab';
import { ArquivosTab } from '../components/ArquivosTab';
import { buildGanttModel, isTaskDone, localDateToIso } from '../components/workspaceHelpers';

type WorkspaceTab = 'gantt' | 'lista' | 'config' | 'timeline' | 'arquivos';

interface UserLite {
  id: string;
  name: string;
  avatar?: string;
}

const ZOOMS: { id: TroiaGanttZoom; label: string }[] = [
  { id: 'day', label: 'Dia' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mês' },
  { id: 'today', label: 'Hoje' },
];

export const ProjectWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: gantt, isLoading, isError } = useProjectGantt(id);
  const { data: users } = useUsersDropdown();
  // `ProjectResponse` (detalhe/gantt) não carrega `clientName` (só `ProjectListItem`,
  // enrichment da listagem) — resolve o nome do cliente aqui via clientId, mesmo
  // padrão de resolução usado pra leaderId/memberIds (usersById).
  const { data: customers } = useCustomersDropdown();
  const updateTask = useUpdateProjectTask();

  const [activeTab, setActiveTab] = useState<WorkspaceTab>('gantt');
  const [zoom, setZoom] = useState<TroiaGanttZoom>('week');
  const [drawerTaskId, setDrawerTaskId] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<TaskModalMode | null>(null);
  const [modalTaskId, setModalTaskId] = useState<string | null>(null);

  const usersById = useMemo(() => {
    const map = new Map<string, UserLite>();
    (users ?? []).forEach((u) => map.set(u.id, { id: u.id, name: u.name, avatar: u.avatar }));
    return map;
  }, [users]);

  const clientsById = useMemo(() => {
    const map = new Map<string, string>();
    (customers ?? []).forEach((c) => map.set(c.id, c.name));
    return map;
  }, [customers]);

  const tasks = gantt?.tasks ?? [];
  const phases = gantt?.project.phases ?? [];

  const ganttModel = useMemo(() => (gantt ? buildGanttModel(gantt) : { tasks: [], links: [] }), [gantt]);

  // Chave que força o remount do adapter quando os dados do servidor mudam
  // (o SVAR copia a config inicial para seu store; props não propagam sozinhas).
  const ganttKey = useMemo(() => {
    const sig = tasks
      .map(
        (t) =>
          `${t.id}:${t.startDate}:${t.endDate}:${t.isMilestone ? 1 : 0}:${isTaskDone(t) ? 1 : 0}:${t.blocked ? 1 : 0}:${t.phaseId ?? ''}`,
      )
      .join('|');
    return `${zoom}-${sig}`;
  }, [tasks, zoom]);

  const drawerTask = useMemo(
    () => tasks.find((t) => t.id === drawerTaskId) ?? null,
    [tasks, drawerTaskId],
  );
  const modalTask = useMemo(
    () => tasks.find((t) => t.id === modalTaskId) ?? null,
    [tasks, modalTaskId],
  );

  const handleSelectTask = (taskId: string) => {
    // Ignora seleção de summary (fase) — só tarefas abrem o drawer.
    if (tasks.some((t) => t.id === taskId)) setDrawerTaskId(taskId);
  };

  const handleTaskDatesChange = (taskId: string, start: Date, end: Date) => {
    if (!id) return;
    if (!tasks.some((t) => t.id === taskId)) return; // ignora summary
    updateTask.mutate({
      projectId: id,
      taskId,
      data: { startDate: localDateToIso(start), endDate: localDateToIso(end) },
    });
  };

  // Reaproveitado pelo drawer ("Editar") e pelo menu de contexto do Gantt
  // (mesma ação, sem depender do drawer estar aberto).
  const openEditModalFor = (taskId: string) => {
    setModalTaskId(taskId);
    setModalMode('edit');
  };

  const openEditFromDrawer = () => {
    if (!drawerTaskId) return;
    openEditModalFor(drawerTaskId);
    setDrawerTaskId(null);
  };

  // Idem para "Abrir ticket" — mesma navegação do `TaskDrawer`.
  const openTicketFor = (taskId: string) => {
    const t = tasks.find((x) => x.id === taskId);
    if (t?.ticket?.ticketId) navigate(`/helpdesk/tickets/${t.ticket.ticketId}`);
  };

  const closeModal = () => {
    setModalMode(null);
    setModalTaskId(null);
  };

  if (isLoading) {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)', color: 'var(--theme-muted-text)' }}
        data-testid="project-ws-loading"
      >
        Carregando projeto...
      </div>
    );
  }

  if (isError || !gantt) {
    return (
      <div
        className="h-full flex flex-col items-center justify-center gap-2"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
        data-testid="project-ws-error"
      >
        <p style={{ color: 'var(--theme-muted-text)' }}>Não foi possível carregar o projeto.</p>
        <button
          type="button"
          className="text-sm"
          style={{ color: 'var(--theme-primary)' }}
          onClick={() => navigate('/helpdesk/projects')}
          data-testid="project-ws-error-back"
        >
          Voltar para Projetos
        </button>
      </div>
    );
  }

  const { project, progress } = gantt;
  const badge = getProjectStatusPill(project.status);
  const pct = Math.max(0, Math.min(100, progress?.pct ?? 0));
  const clientName = project.clientId ? clientsById.get(project.clientId) : undefined;

  const team: UserLite[] = [project.leaderId, ...(project.memberIds ?? [])]
    .filter((uid, i, arr) => !!uid && arr.indexOf(uid) === i)
    .map((uid) => usersById.get(uid))
    .filter((u): u is UserLite => !!u);

  const ganttTab = (
    // Sidebar ~30% mais estreita que o original (300px → 210px) — mais espaço
    // pro gráfico, mesma altura/gap.
    <div className="h-full grid grid-cols-1 xl:grid-cols-[1fr_210px] gap-4 p-4">
      <div
        className="min-w-0 flex flex-col rounded-lg overflow-hidden"
        style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
      >
        {/* Zoom — dentro do card, acima do Gantt (mockup .det-main > .gz + .gt) */}
        <div className="flex items-center gap-1.5 flex-shrink-0 px-3.5 pt-3 pb-2.5" data-testid="project-ws-zoom">
          {ZOOMS.map((z) => {
            const active = zoom === z.id;
            return (
              <button
                key={z.id}
                type="button"
                onClick={() => setZoom(z.id)}
                className="h-7 px-3 rounded-[7px] text-xs font-semibold transition-colors"
                style={{
                  backgroundColor: active ? 'var(--theme-primary)' : 'var(--theme-card-background)',
                  color: active ? '#ffffff' : 'var(--theme-muted-text)',
                  border: active ? '1px solid var(--theme-primary)' : '1px solid var(--theme-card-border)',
                }}
                data-testid={`project-ws-zoom-${z.id}`}
              >
                {z.label}
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-h-0">
          {tasks.length === 0 ? (
            <div
              className="h-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-muted-text)' }}
              data-testid="project-ws-gantt-empty"
            >
              Nenhuma tarefa no cronograma. Use "+ Nova tarefa" para começar.
            </div>
          ) : (
            <Suspense
              fallback={
                <div
                  className="h-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-muted-text)' }}
                >
                  Carregando cronograma...
                </div>
              }
            >
              <TroiaGantt
                key={ganttKey}
                tasks={ganttModel.tasks}
                links={ganttModel.links}
                zoom={zoom}
                onSelectTask={handleSelectTask}
                onTaskDatesChange={handleTaskDatesChange}
                onOpenTicket={openTicketFor}
                onEditTask={openEditModalFor}
              />
            </Suspense>
          )}
        </div>
      </div>

      <div className="overflow-y-auto">
        <WorkspaceSidePanel gantt={gantt} usersById={usersById} onGoToConfig={() => setActiveTab('config')} />
      </div>
    </div>
  );

  const listaTab = (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
      <ProjectTasksTable tasks={tasks} phases={phases} onSelectTask={(t) => setDrawerTaskId(t.id)} />
    </div>
  );

  const tabs: TabItem[] = [
    { id: 'gantt', label: 'Gantt', icon: 'calendar', content: ganttTab },
    {
      id: 'lista',
      label: 'Lista',
      icon: 'list',
      ...(tasks.length > 0 ? { badge: tasks.length } : {}),
      content: listaTab,
    },
    { id: 'config', label: 'Configurações', icon: 'settings', content: <ProjectConfigTab project={project} /> },
    {
      id: 'timeline',
      label: 'Timeline',
      icon: 'activity',
      content: (
        <TimelineTab tasks={tasks} phases={phases} projectName={project.name} projectCreatedAt={project.createdAt} />
      ),
    },
    {
      id: 'arquivos',
      label: 'Arquivos',
      icon: 'file-text',
      ...(project.attachments && project.attachments.length > 0 ? { badge: project.attachments.length } : {}),
      content: <ArquivosTab projectId={project.id} />,
    },
  ];

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }} data-testid="project-ws-page">
      {/* Header — toolbar 3 zonas: breadcrumbs (esquerda) + status/progresso/equipe/ações (direita) */}
      <div
        className="flex-shrink-0 px-4 border-b flex items-center gap-3"
        style={{ height: 56, backgroundColor: 'var(--theme-header-background)', borderColor: 'var(--theme-header-border)' }}
      >
        {/* Cor do projeto + breadcrumb shared (F25-F31: 1º segmento clicável,
            mobileLegend no lugar dos segmentos escondidos — nunca seta-voltar) */}
        <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: project.color }} />
        <div className="flex items-center gap-1.5 min-w-0">
          <Breadcrumb
            items={[
              { label: 'Help Desk', onNavigate: () => navigate('/helpdesk/tickets'), testid: 'project-ws-crumb-helpdesk' },
              { label: 'Projetos', onNavigate: () => navigate('/helpdesk/projects'), testid: 'project-ws-back' },
              { label: project.name, testid: 'project-ws-crumb-current' },
            ]}
            mobileLegend="Help Desk · Projeto"
            data-testid="project-ws-breadcrumb"
          />
          {clientName ? (
            <span
              className="hidden md:inline whitespace-nowrap flex-shrink-0"
              style={{ fontSize: 14, color: 'var(--theme-muted-text)' }}
            >
              — {clientName}
            </span>
          ) : null}
        </div>

        {/* Ações à direita */}
        <div className="flex items-center gap-3 ml-auto flex-shrink-0">
          {/* F62/F65/F66: StatusPill, 'Concluído' verde, dot default */}
          <StatusPill variant={badge.variant} label={badge.label} data-testid="project-ws-status" />

          {/* Progresso */}
          <div className="hidden lg:flex items-center gap-2">
            <span className="whitespace-nowrap" style={{ fontSize: 12, color: 'var(--theme-muted-text)' }}>
              {progress?.done ?? 0}/{progress?.total ?? 0} · {pct}%
            </span>
            <div className="w-28 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--theme-border)' }}>
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, backgroundColor: pct === 100 ? 'var(--theme-success-color)' : 'var(--theme-primary)' }}
              />
            </div>
          </div>

          {team.length > 0 && (
            <div className="hidden md:block flex-shrink-0">
              <MemberAvatarList members={team} canRemoveMembers={false} size="sm" limit={5} />
            </div>
          )}

          {/* Ações — desktop: botões completos; mobile: ••• overflow (mockup .m-appbar i-more-v) */}
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            <Button variant="secondary" size="sm" className="!h-9 !px-3" icon="link" onClick={() => setModalMode('link')} data-testid="project-ws-link-ticket">
              Vincular ticket
            </Button>
            <Button size="sm" className="!h-9 !px-3" icon="plus" onClick={() => setModalMode('create')} data-testid="project-ws-new-task">
              Nova tarefa
            </Button>
          </div>
          <div className="sm:hidden flex-shrink-0" data-testid="project-ws-actions-mobile">
            <CardActionsMenu
              actions={[
                { label: 'Nova tarefa', icon: 'plus', onClick: () => setModalMode('create') },
                { label: 'Vincular ticket', icon: 'link', onClick: () => setModalMode('link') },
              ]}
            />
          </div>
        </div>
      </div>

      <Tabs
        variant="underline"
        pageLevel
        fullBleed
        activeTab={activeTab}
        onChange={(t) => setActiveTab(t as WorkspaceTab)}
        tabs={tabs}
      />

      {/* Drawer de detalhe da tarefa */}
      <TaskDrawer
        isOpen={drawerTaskId !== null}
        onClose={() => setDrawerTaskId(null)}
        task={drawerTask}
        allTasks={tasks}
        phases={phases}
        onEdit={openEditFromDrawer}
      />

      {/* Modal de tarefa (create / edit / link) */}
      {modalMode && id && (
        <TaskModal
          isOpen={modalMode !== null}
          onClose={closeModal}
          mode={modalMode}
          projectId={id}
          pipelineId={project.pipelineId}
          phases={phases}
          allTasks={tasks}
          task={modalTask}
        />
      )}
    </div>
  );
};

ProjectWorkspacePage.displayName = 'ProjectWorkspacePage';
